import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  InputAdornment,
  Chip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import SecurityIcon from '@mui/icons-material/Security';
import BugReportIcon from '@mui/icons-material/BugReport';
import ForumIcon from '@mui/icons-material/Forum';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import { useNavigate } from 'react-router-dom';
import { searchService, SearchResult } from '../../services/searchService';
import { isCoffeeQuery } from '../../utils/easterEggs';

const COFFEE_RESULT: SearchResult = {
  type: 'scherz',
  id: 0,
  title: 'Ungepatchter Kaffeeautomat',
  subtitle: 'Easter Egg, wird nicht gespeichert',
  url: '/kaffee',
};

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
}

const TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  projekt: { label: 'Projekt', icon: <FolderIcon fontSize="small" />, color: '#42a5f5' },
  kunde: { label: 'Kunde', icon: <BusinessIcon fontSize="small" />, color: '#ffa726' },
  mitarbeiter: { label: 'Mitarbeiter', icon: <PeopleIcon fontSize="small" />, color: '#ce93d8' },
  ansprechpartner: { label: 'Ansprechpartner', icon: <ContactPhoneIcon fontSize="small" />, color: '#26c6da' },
  audit: { label: 'Audit', icon: <SecurityIcon fontSize="small" />, color: '#3dd47e' },
  schwachstelle: { label: 'Schwachstelle', icon: <BugReportIcon fontSize="small" />, color: '#ef5350' },
  diskussion: { label: 'Diskussion', icon: <ForumIcon fontSize="small" />, color: '#9575cd' },
  angebot: { label: 'Angebot', icon: <RequestQuoteIcon fontSize="small" />, color: '#66bb6a' },
  scherz: { label: 'Scherz', icon: <LocalCafeIcon fontSize="small" />, color: '#e0875a' },
};

const SearchDialog: React.FC<SearchDialogProps> = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /* Reset state when dialog opens */
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  /* Debounced search */
  const handleSearch = useCallback(
    (value: string) => {
      setQuery(value);
      setSelectedIndex(0);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (value.trim().length === 0) {
        setResults([]);
        setLoading(false);
        return;
      }

      if (isCoffeeQuery(value)) {
        setResults([COFFEE_RESULT]);
        setLoading(false);
        return;
      }

      setLoading(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const response = await searchService.search(value.trim());
          setResults(response.results);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    []
  );

  /* Navigate to result */
  const handleSelect = useCallback(
    (result: SearchResult, e?: React.MouseEvent) => {
      if (e && (e.ctrlKey || e.metaKey || e.button === 1)) {
        e.preventDefault();
        window.open(result.url, '_blank');
        return;
      }
      onClose();
      navigate(result.url);
    },
    [navigate, onClose]
  );

  /* Keyboard navigation */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault();
        handleSelect(results[selectedIndex]);
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [results, selectedIndex, handleSelect, onClose]
  );

  /* Cleanup timeout */
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDark ? '#212744' : '#ffffff',
          border: `1px solid ${theme.palette.divider}`,
          mt: '10vh',
          maxHeight: '70vh',
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' },
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Search input */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Suche nach Projekten, Kunden, Diskussionen, Audits..."
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="standard"
            autoFocus
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : (
                <InputAdornment position="end">
                  <Chip
                    label="ESC"
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      bgcolor: isDark ? '#2e3560' : '#e8ecf0',
                      color: theme.palette.text.secondary,
                    }}
                  />
                </InputAdornment>
              ),
              sx: { fontSize: '1rem' },
            }}
          />
        </Box>

        {/* Results */}
        {results.length > 0 && (
          <List sx={{ py: 1, maxHeight: '50vh', overflow: 'auto' }}>
            {results.map((result, index) => {
              const config = TYPE_CONFIG[result.type] || {
                label: result.type,
                icon: <SearchIcon fontSize="small" />,
                color: theme.palette.text.secondary,
              };

              return (
                <ListItemButton
                  key={`${result.type}-${result.id}`}
                  selected={index === selectedIndex}
                  onClick={(e) => handleSelect(result, e)}
                  onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); handleSelect(result, e); } }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  sx={{
                    mx: 1,
                    borderRadius: 2,
                    '&.Mui-selected': {
                      bgcolor: isDark ? '#2c3358' : '#eef1f5',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: config.color }}>
                    {config.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {result.title}
                      </Typography>
                    }
                    secondary={result.subtitle}
                  />
                  <Chip
                    label={config.label}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      bgcolor: `${config.color}20`,
                      color: config.color,
                      fontWeight: 600,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}

        {/* Empty state */}
        {query.trim().length > 0 && !loading && results.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Keine Ergebnisse für "{query}"
            </Typography>
          </Box>
        )}

        {/* Initial hint */}
        {query.trim().length === 0 && (
          <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Suchbegriff eingeben, um Projekte, Kunden, Diskussionen, Audits und mehr zu finden
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
